import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft,
  MessageCircle,
  Phone,
  Video,
  Search,
  MoreVertical,
  Trash2,
  X,
  User,
  Clock,
  Circle
} from 'lucide-react';
import { chatService, getLawyerChats, subscribeToLawyerChats, subscribeToCompletedLawyerChats, deleteChat, endConversation, cleanupOversizedChats } from '../services/chatService';
import { endConsultation, canEndConsultation, isConsultationCompleted } from '../services/consultationService';
import toast from 'react-hot-toast';

const LawyerChats = () => {
  const navigate = useNavigate();
  const { user, language } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'completed'
  const [showMenu, setShowMenu] = useState(null);
  const [activeChats, setActiveChats] = useState([]);
  const [completedChats, setCompletedChats] = useState([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    english: {
      chats: 'Chats',
      back: 'Back',
      searchChats: 'Search chats...',
      noChats: 'No chats found',
      startChat: 'Start a new chat',
      legalConsultations: 'Your client consultations',
      online: 'Online',
      message: 'Message',
      call: 'Call',
      video: 'Video',
      noChatsDesc: 'You haven\'t received any client messages yet. Cases will appear here when clients start conversations!',
      allChats: 'All Chats',
      activeChats: 'Active',
      completedChats: 'Completed',
      deleteChat: 'Delete Chat',
      endConversation: 'End Conversation',
      confirmDelete: 'Are you sure you want to delete this chat?',
      confirmEnd: 'Are you sure you want to end this conversation?'
    },
    hindi: {
      chats: 'चैट',
      back: 'वापस',
      searchChats: 'चैट खोजें...',
      noChats: 'कोई चैट नहीं मिली',
      startChat: 'नई चैट शुरू करें',
      legalConsultations: 'आपके क्लाइंट परामर्श',
      online: 'ऑनलाइन',
      message: 'संदेश',
      call: 'कॉल',
      video: 'वीडियो',
      noChatsDesc: 'आपको अभी तक कोई क्लाइंट संदेश नहीं मिला है। जब क्लाइंट बातचीत शुरू करेंगे तो केस यहां दिखाई देंगे!',
      allChats: 'सभी चैट',
      activeChats: 'सक्रिय',
      completedChats: 'पूर्ण',
      deleteChat: 'चैट हटाएं',
      endConversation: 'बातचीत समाप्त करें',
      confirmDelete: 'क्या आप वाकई इस चैट को हटाना चाहते हैं?',
      confirmEnd: 'क्या आप वाकई इस बातचीत को समाप्त करना चाहते हैं?'
    }
  };

  const t = translations[language] || translations.english;

  // Load lawyer's chat list from Firestore with real-time updates
  useEffect(() => {
    if (!user?.uid) return;
    
    console.log('🔄 Setting up real-time chat subscriptions for user:', user.uid);
    
    // Subscribe to active and pending chats
    let unsubscribeActive = null;
    let unsubscribeCompleted = null;
    
    try {
      unsubscribeActive = subscribeToLawyerChats(user.uid, (activeChatsData) => {
        console.log('📋 Active/Pending chats received:', activeChatsData);
        console.log('🔍 Active chats count:', activeChatsData?.length || 0);
        console.log('🔍 Active chats data structure:', activeChatsData);
        if (activeChatsData && activeChatsData.length > 0) {
          console.log('🔍 First active chat details:', activeChatsData[0]);
        }
        setActiveChats(activeChatsData || []);
      });
      
      // Subscribe to completed chats
      unsubscribeCompleted = subscribeToCompletedLawyerChats(user.uid, (completedChatsData) => {
        console.log('📋 Completed chats received:', completedChatsData);
        setCompletedChats(completedChatsData || []);
      });
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error setting up chat subscriptions:', error);
      setLoading(false);
      setActiveChats([]);
      setCompletedChats([]);
    }
    
    // Cleanup subscriptions
    return () => {
      console.log('🧹 Cleaning up chat subscriptions');
      if (unsubscribeActive && typeof unsubscribeActive === 'function') {
        unsubscribeActive();
      }
      if (unsubscribeCompleted && typeof unsubscribeCompleted === 'function') {
        unsubscribeCompleted();
      }
    };
  }, [user?.uid]);

  // Get chats based on active filter
  const getFilteredChats = () => {
    let chatsToShow = [];
    
    console.log('🔍 Filtering chats - activeFilter:', activeFilter);
    console.log('🔍 Active chats count:', activeChats.length);
    console.log('🔍 Completed chats count:', completedChats.length);
    
    if (activeFilter === 'all') {
      chatsToShow = [...activeChats, ...completedChats];
    } else if (activeFilter === 'active') {
      chatsToShow = activeChats;
      console.log('🔍 Active filter selected - chats to show:', chatsToShow);
    } else if (activeFilter === 'completed') {
      chatsToShow = completedChats;
    }
    
    console.log('🔍 Total chats before search filter:', chatsToShow.length);
    
    // Apply search filter
    const filteredChats = chatsToShow.filter(chat => {
      const matchesSearch = (chat.clientName || 'Client')?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (chat.lastMessage || '')?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
    
    console.log('🔍 Final filtered chats count:', filteredChats.length);
    return filteredChats;
  };
  
  const filteredChats = getFilteredChats();

  const handleChatClick = (chat) => {
    try {
      console.log('🖱️ Chat clicked:', chat);
      
      if (!chat || !chat.chatId) {
        console.error('❌ Invalid chat data:', chat);
        toast.error('Invalid chat data. Please try again.');
        return;
      }
      
      navigate(`/lawyer-chat-conversation/${chat.chatId}`, { 
        state: { 
          chatData: chat // Pass the complete chat object
        } 
      });
    } catch (error) {
      console.error('❌ Error navigating to chat:', error);
      toast.error('Failed to open chat. Please try again.');
    }
  };

  const handleDeleteChat = async (chatId) => {
    if (!confirm(t.confirmDelete)) return;
    
    try {
      const result = await deleteChat(chatId);
      if (result.success) {
        toast.success('Chat deleted successfully');
        // The real-time subscription will automatically update the chat lists
        // No need to manually update state
      } else {
        toast.error('Error deleting chat');
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast.error('Error deleting chat');
    }
  };

  const handleEndConversation = async (chatId) => {
    if (!confirm(t.confirmEnd)) return;
    
    try {
      const result = await endConsultation(chatId, user?.uid, 'lawyer');
      if (result.success) {
        toast.success('Consultation ended successfully');
        // The real-time subscription will automatically update the chat status
        // and move it from activeChats to completedChats
      } else {
        toast.error('Error ending consultation');
      }
    } catch (error) {
      console.error('Error ending consultation:', error);
      toast.error('Error ending consultation');
    }
  };

  const handleCleanupOversizedChats = async () => {
    try {
      toast.loading('Cleaning up oversized chats...');
      const result = await cleanupOversizedChats();
      
      if (result.success) {
        toast.dismiss();
        if (result.cleanedCount > 0) {
          toast.success(`Cleaned up ${result.cleanedCount} oversized chat documents`);
        } else {
          toast.success('No oversized chats found');
        }
      } else {
        toast.dismiss();
        toast.error(result.error || 'Failed to cleanup chats');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error cleaning up chats:', error);
      toast.error('Failed to cleanup chats');
    }
  };



  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    try {
      const date = new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Just now';
      }
      
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Just now';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-gray-400';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  const handleManualMigration = async () => {
    if (!confirm('Are you sure you want to migrate all pending chats to active status?')) {
      return;
    }
    try {
      const result = await chatService.migratePendingChatsToActive(user.uid);
      if (result.success) {
        toast.success('Pending chats migrated successfully!');
        // The real-time subscription will automatically update the chat lists
        // No need to manually update state
      } else {
        toast.error('Error migrating chats:', result.error);
      }
    } catch (error) {
      console.error('Error migrating chats:', error);
      toast.error('Error migrating chats');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/lawyer-dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t.chats}</h1>
              <p className="text-sm text-gray-600">{t.legalConsultations}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-gray-700">{t.online}</span>
          </div>
        </div>
        </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t.searchChats}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
      </div>

      {/* Filter Tabs */}
          <div className="flex space-x-1">
          <button
            onClick={() => setActiveFilter('all')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
              activeFilter === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
              {t.allChats} ({activeChats.length + completedChats.length})
          </button>
          <button
            onClick={() => setActiveFilter('active')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
              activeFilter === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
              {t.activeChats} ({activeChats.length})
          </button>
          <button
            onClick={() => setActiveFilter('completed')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
              activeFilter === 'completed'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
              {t.completedChats} ({completedChats.length})
          </button>
        </div>
        


        </div>
      </div>

      {/* Chat List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading chats...</p>
          </div>
        ) : filteredChats.length > 0 ? (
          filteredChats.map((chat, index) => (
              <motion.div
              key={chat.chatId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => handleChatClick(chat)}
            >
              <div className="flex items-start gap-3 relative">
                {/* Client Avatar */}
                <div className="relative flex-shrink-0">
                  <img
                    src={chat.clientPhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=48&h=48&fit=crop&crop=face'}
                    alt={chat.clientName}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                  />
                  {/* Status indicator */}
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 ${getStatusColor(chat.status)} rounded-full border-2 border-white`}></div>
                </div>

                {/* Chat Content */}
                <div className="flex-1 min-w-0">
                  {/* Row 1: Name and Status */}
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 truncate text-base">
                      {chat.clientName || 'Client'}
                    </h3>
                    {/* Status Badge */}
                    <span className={`capitalize px-2 py-0.5 rounded-full text-xs font-medium ${
                      chat.status === 'active' ? 'bg-green-100 text-green-700' :
                      chat.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {chat.status || 'pending'}
                    </span>
                  </div>

                  {/* Row 2: Last Message */}
                  <div className="mb-2">
                    <p className="text-sm text-gray-600 truncate">
                      {chat.lastMessage || 'No messages yet'}
                    </p>
                  </div>
                </div>

                {/* Right Side: Menu, Date, and Action Buttons */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-2">
                  {/* Three Dots Menu */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(showMenu === chat.chatId ? null : chat.chatId);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-600" />
                    </button>
                    
                    {showMenu === chat.chatId && (
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[150px]">
                        {canEndConsultation(chat) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEndConversation(chat.chatId);
                              setShowMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          >
                            <X className="w-4 h-4" />
                            End Consultation
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChat(chat.chatId);
                            setShowMenu(null);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t.deleteChat}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Date Formatting */}
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(chat.lastMessageTime)}
                  </span>
                  
                  {/* Call and Video Buttons - Only for active consultations */}
                  {!isConsultationCompleted(chat) && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle call
                        }}
                        className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors shadow-sm"
                        title="Call"
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle video call
                        }}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm"
                        title="Video Call"
                      >
                        <Video className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Booking Time at Bottom Left */}
                {chat.bookingDate && (
                  <div className="absolute bottom-0 left-0 flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>Booked {formatTimestamp(chat.bookingDate)}</span>
                    {chat.clientCity && chat.clientCity !== 'City not specified' && (
                      <>
                        <span>•</span>
                        <span>{chat.clientCity}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="bg-white rounded-xl p-8 text-center">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.noChats}</h3>
            <p className="text-gray-500 text-sm">{t.noChatsDesc}</p>
            </div>
        )}
      </div>
    </div>
  );
};

export default LawyerChats;
